import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useT, pickLang } from "../translations";
import ProductSheet from "../components/public/ProductSheet";
import WifiSheet from "../components/public/WifiSheet";
import MenuDrawer from "../components/public/MenuDrawer";

const PublicMenu = () => {
  const [params] = useSearchParams();
  const area = params.get("area") || params.get("table");
  const { lang } = useLanguage();
  const t = useT(lang);

  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // view: 'categories' | 'category' | 'search'
  const [view, setView] = useState("categories");
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showWifi, setShowWifi] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/settings"),
      api.get("/categories?active_only=true"),
      api.get("/products?active_only=true"),
    ]).then(([s, c, p]) => {
      setSettings(s.data);
      setCategories(c.data);
      setProducts(p.data);
      setLoading(false);
      api.post("/analytics", { pageType: "menu", areaId: area || null, language: lang }).catch(() => {});
    }).catch(() => setLoading(false));
  }, [area, lang]);

  // Dynamic SEO: title + favicon + meta description
  useEffect(() => {
    if (!settings) return;
    const title = lang === "en" ? settings.siteTitleEN : settings.siteTitleTR;
    if (title) document.title = title;
    const desc = lang === "en" ? settings.metaDescriptionEN : settings.metaDescriptionTR;
    if (desc) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
      meta.content = desc;
    }
    if (settings.favicon) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = settings.favicon;
    }
  }, [settings, lang]);

  const productsByCat = useMemo(() => {
    const map = {};
    categories.forEach((c) => (map[c.id] = []));
    products.forEach((p) => { if (map[p.categoryId]) map[p.categoryId].push(p); });
    return map;
  }, [categories, products]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => {
      const txt = `${p.nameTR} ${p.nameEN} ${p.shortDescriptionTR} ${p.shortDescriptionEN}`.toLowerCase();
      return txt.includes(q);
    });
  }, [products, search]);

  const formatPrice = (p) => `${settings?.currency || "₺"} ${Number(p).toFixed(2)}`;

  const openCategory = (cat) => {
    setActiveCat(cat);
    setView("category");
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const goBack = () => {
    if (view === "category" || view === "search") {
      setView("categories");
      setActiveCat(null);
      setSearch("");
      setSearchOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zbeach-bg flex flex-col items-center justify-center gap-4" data-testid="menu-loading">
        <img src="/zbeach-logo.png" alt="Z Beach" className="w-40 sm:w-48 animate-pulse" />
        <div className="text-zbeach-gold/60 text-xs uppercase tracking-[0.3em] animate-pulse">Menü yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zbeach-bg flex flex-col" data-testid="public-menu">
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-zbeach-surface/95 backdrop-blur-md border-b border-zbeach-gold/15">
        <div className="relative max-w-2xl mx-auto px-4 py-4 flex items-center justify-between min-h-[64px]">
          <button onClick={() => setDrawerOpen(true)} className="w-10 h-10 -ml-2 flex items-center justify-center text-zbeach-dark" aria-label="Menü" data-testid="header-hamburger">
            <i className="fa-solid fa-bars text-xl" />
          </button>

          <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
            {settings?.logo ? (
              <img src={settings.logo} alt={settings?.businessName} className="h-10 w-auto object-contain mx-auto" />
            ) : (
              <>
                <div className="font-serif text-xl tracking-[0.2em] text-gold-gradient font-semibold leading-none" data-testid="brand-logo">{(settings?.businessName || "Z BEACH").toUpperCase()}</div>
                <div className="font-hand text-zbeach-gold text-sm mt-0.5">{lang === "en" ? settings?.heroSubtitleEN : settings?.heroSubtitleTR}</div>
              </>
            )}
          </div>

          <button onClick={() => setSearchOpen(!searchOpen)} className="w-10 h-10 -mr-2 flex items-center justify-center text-zbeach-dark" aria-label="Ara" data-testid="header-search">
            <i className={`fa-solid ${searchOpen ? "fa-xmark" : "fa-magnifying-glass"} text-xl`} />
          </button>
        </div>
        {searchOpen && (
          <div className="px-4 pb-3" data-testid="header-search-bar">
            <input
              autoFocus
              value={search}
              onChange={(e) => { setSearch(e.target.value); if (e.target.value) setView("search"); else setView("categories"); }}
              placeholder={t.searchPlaceholder}
              className="w-full px-4 py-3 rounded-full bg-zbeach-bg border border-zbeach-gold/20 text-sm focus:outline-none focus:border-zbeach-turquoise"
              data-testid="search-input"
            />
          </div>
        )}
        {area && (
          <div className="px-4 pb-2 flex justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zbeach-turquoise/10 text-zbeach-turquoise text-[11px] uppercase tracking-wider" data-testid="area-badge">
              <i className="fa-solid fa-chair text-[10px]" />
              {decodeURIComponent(area).replace(/-/g, " ")}
            </span>
          </div>
        )}
      </header>

      {/* MAIN */}
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 pt-5 pb-24">
        <AnimatePresence mode="wait">
          {view === "categories" && (
            <motion.div key="cats" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <CategoriesGrid categories={categories} productsByCat={productsByCat} lang={lang} onOpen={openCategory} />
            </motion.div>
          )}
          {view === "category" && activeCat && (
            <motion.div key={`cat-${activeCat.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <ProductList
                category={activeCat}
                products={productsByCat[activeCat.id] || []}
                lang={lang} t={t}
                onProduct={setSelected}
                formatPrice={formatPrice}
              />
            </motion.div>
          )}
          {view === "search" && (
            <motion.div key="search-view" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <h2 className="font-serif text-2xl text-zbeach-dark uppercase tracking-wide mb-4">{lang === "en" ? "Search" : "Arama"}</h2>
              {searchResults.length === 0 ? (
                <div className="text-center py-12 text-zbeach-muted">{t.noResults}</div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((p) => (
                    <ProductRow key={p.id} product={p} lang={lang} t={t} onClick={() => setSelected(p)} formatPrice={formatPrice} />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Back button (sticky bottom-left) */}
      {view !== "categories" && (
        <button
          onClick={goBack}
          className="fixed bottom-4 left-4 z-30 w-14 h-14 rounded-tr-2xl rounded-bl-2xl rounded-tl-md rounded-br-md bg-zbeach-turquoise text-white flex items-center justify-center shadow-lg shadow-zbeach-turquoise/30 hover:bg-zbeach-turquoise/90 transition"
          data-testid="back-button"
          aria-label="Geri"
        >
          <i className="fa-solid fa-chevron-left text-lg" />
        </button>
      )}

      {/* Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <MenuDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            settings={settings}
            onOpenWifi={() => setShowWifi(true)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selected && (
          <ProductSheet
            product={selected}
            onClose={() => setSelected(null)}
            lang={lang}
            t={t}
            formatPrice={formatPrice}
          />
        )}
        {showWifi && <WifiSheet settings={settings} onClose={() => setShowWifi(false)} t={t} />}
      </AnimatePresence>
    </div>
  );
};

/* ============ CATEGORIES GRID ============ */
const CategoriesGrid = ({ categories, productsByCat, lang, onOpen }) => {
  if (categories.length === 0) {
    return <div className="text-center py-20 text-zbeach-muted">Henüz kategori eklenmemiş.</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="categories-grid">
      {categories.map((cat, i) => {
        const items = productsByCat[cat.id] || [];
        const cover = cat.image || items[0]?.image;
        return (
          <motion.button
            key={cat.id}
            onClick={() => onOpen(cat)}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.4 }}
            whileTap={{ scale: 0.97 }}
            className="relative overflow-hidden rounded-2xl bg-zbeach-surface border border-zbeach-gold/15 shadow-sm hover:shadow-md transition-all group"
            data-testid={`category-card-${cat.slug}`}
          >
            <div className="aspect-[4/5] sm:aspect-square w-full bg-zbeach-sand overflow-hidden">
              {cover ? (
                <img src={cover} alt={pickLang(cat, lang, "name")} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zbeach-gold/40">
                  <i className={`fa-solid ${cat.icon ? `fa-${cat.icon}` : "fa-utensils"} text-4xl`} />
                </div>
              )}
            </div>
            <div className="absolute left-2 right-2 bottom-2 bg-zbeach-turquoise/95 backdrop-blur-sm py-2 px-3 rounded-md text-white text-center">
              <div className="text-xs sm:text-sm font-medium uppercase tracking-wider truncate" data-testid={`category-name-${cat.slug}`}>
                {pickLang(cat, lang, "name")}
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

/* ============ PRODUCT LIST ============ */
const ProductList = ({ category, products, lang, t, onProduct, formatPrice }) => {
  return (
    <div data-testid={`product-list-${category.slug}`}>
      <header className="mb-5">
        <h1 className="font-serif text-3xl text-zbeach-dark uppercase tracking-wider font-semibold" data-testid="category-title">{pickLang(category, lang, "name")}</h1>
        {pickLang(category, lang, "description") && (
          <p className="text-zbeach-muted text-sm mt-1">{pickLang(category, lang, "description")}</p>
        )}
      </header>
      {products.length === 0 ? (
        <div className="text-center py-16 text-zbeach-muted">—</div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <ProductRow key={p.id} product={p} lang={lang} t={t} onClick={() => onProduct(p)} formatPrice={formatPrice} />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductRow = ({ product, lang, t, onClick, formatPrice }) => {
  const name = pickLang(product, lang, "name");
  const desc = pickLang(product, lang, "shortDescription");
  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className="flex gap-3 bg-zbeach-surface rounded-2xl p-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition border border-zbeach-gold/10"
      data-testid={`product-row-${product.id}`}
    >
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-zbeach-sand">
        {product.image ? (
          <img src={product.image} alt={name} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zbeach-gold/40"><i className="fa-solid fa-image text-xl" /></div>
        )}
        {!product.isAvailable && (
          <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-[9px] text-white text-center px-1 uppercase tracking-wider">{t.unavailable}</div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <h3 className="font-serif text-base sm:text-lg text-zbeach-dark font-semibold leading-tight">{name}</h3>
          {desc && <p className="text-xs text-zbeach-muted mt-1 line-clamp-2">{desc}</p>}
        </div>
        <div className="flex items-end justify-between mt-1.5">
          <div className="flex items-baseline gap-2">
            {product.discountedPrice ? (
              <>
                <span className="font-serif text-lg font-semibold text-zbeach-turquoise" data-testid={`price-${product.id}`}>{formatPrice(product.discountedPrice)}</span>
                <span className="text-xs text-zbeach-muted line-through">{formatPrice(product.price)}</span>
              </>
            ) : (
              <span className="font-serif text-lg font-semibold text-zbeach-turquoise" data-testid={`price-${product.id}`}>{formatPrice(product.price)}</span>
            )}
          </div>
          <div className="flex gap-1.5 text-zbeach-muted/80">
            {product.isPopular && <i className="fa-solid fa-fire text-zbeach-gold text-[11px]" title={t.popular} />}
            {product.isNew && <i className="fa-solid fa-sparkles text-zbeach-turquoise text-[11px]" title={t.new} />}
            {product.isChefRecommended && <i className="fa-solid fa-utensils text-zbeach-dark text-[11px]" title={t.chef} />}
            {product.isVegan && <i className="fa-solid fa-leaf text-green-600 text-[11px]" title={t.vegan} />}
            {product.isSpicy && <i className="fa-solid fa-pepper-hot text-red-500 text-[11px]" title={t.spicy} />}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default PublicMenu;
